<script setup>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import ChannelApi from '../../../../../api/channels';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const { accountId } = useAccount();
const route = useRoute();
const router = useRouter();
const store = useStore();
const errorStateMessage = ref(route.query.error);
const channelId = ref(route.query.channel_id);
const selectedShopId = ref('');
const selectedShopCipher = ref('');
const inboxName = ref('');
const shopList = ref([]);
const emptyStateMessage = ref('');
const isFetching = ref(false);
const isCreating = ref(false);

const validationRules = {
  selectedShopId: { required },
  inboxName: { required },
};

const v$ = useVuelidate(validationRules, { selectedShopId, inboxName });

const shopMenuItems = computed(() =>
  shopList.value.map(shop => ({ value: shop.id, label: shop.name }))
);

const tiktokShopAuthorize = async () => {
  // Redirect URL is configured on TikTok Shop partner center console
  const state = btoa(
    JSON.stringify({
      account_id: accountId.value,
    })
  );

  const authUrl = `https://services.tiktokshop.com/open/authorize?service_id=${window.chatwootConfig.tiktokShopServiceId}&state=${state}`;
  window.location.href = authUrl;
};

const fetchTiktokShops = async () => {
  try {
    emptyStateMessage.value = 'Fetching Tiktok Shops...';
    isFetching.value = true;
    const response = await ChannelApi.fetchTiktokShops(
      accountId.value,
      channelId.value
    );

    const {
      data: { data },
    } = response;

    shopList.value = data.shops;
    isFetching.value = false;
  } catch (error) {
    // Ignore error
  }
};

const hasChannelId = () => {
  return channelId.value !== undefined && channelId.value !== null;
};

const handleShopSelect = shopId => {
  v$.value.selectedShopId.$touch();
  v$.value.inboxName.$touch();
  const shop = shopId ? shopList.value.find(s => s.id === shopId) : null;
  if (shop) {
    selectedShopCipher.value = shop.cipher;
    inboxName.value = shop.name;
  } else {
    selectedShopCipher.value = null;
    inboxName.value = '';
  }
};

const showLoader = computed(() => {
  return isFetching.value || isCreating.value;
});

const createInbox = async () => {
  v$.value.$touch();
  if (!v$.value.$error) {
    emptyStateMessage.value = 'Creating Inbox...';
    isCreating.value = true;
    const channelParams = {
      inbox_name: inboxName.value,
      shop_cipher: selectedShopCipher.value,
      shop_id: selectedShopId.value,
      channel_id: channelId.value,
    };
    store
      .dispatch('inboxes/createTiktokInbox', channelParams)
      .then(data => {
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      })
      .catch(() => {
        isCreating.value = false;
      });
  }
};

onMounted(() => {
  if (hasChannelId()) {
    fetchTiktokShops();
  }
});
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.TIKTOKSHOP_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.TIKTOKSHOP_CHANNEL.DESC')"
    />
    <div
      v-if="!hasChannelId()"
      class="flex flex-col items-center justify-center h-[60%]"
    >
      <p class="text-danger mb-5 text-red-400">
        {{ errorStateMessage }}
      </p>
      <img
        src="~dashboard/assets/images/channels/tiktok_shop.png"
        alt="tiktok shop logo"
        class="w-32 h-auto mb-4"
      />
      <a
        href="#"
        class="py-2 px-4 font-semibold rounded-md text-white bg-slate-900 hover:bg-slate-700 duration-150 dark:text-slate-900 dark:bg-white dark:hover:bg-slate-100"
        @click="tiktokShopAuthorize()"
      >
        <span>{{ $t('INBOX_MGMT.ADD.TIKTOKSHOP_CHANNEL.CONNECT') }}</span>
      </a>
    </div>
    <div v-else>
      <LoadingState v-if="showLoader" :message="emptyStateMessage" />
      <form v-else @submit.prevent="createInbox()">
        <div class="w-full">
          <div class="input-wrap" :class="{ error: v$.selectedShopId.$error }">
            <label>
              {{ $t('INBOX_MGMT.ADD.TIKTOK.CHOOSE_SHOP') }}
            </label>
            <ComboBox
              v-model="selectedShopId"
              :options="shopMenuItems"
              :placeholder="$t('INBOX_MGMT.ADD.TIKTOK.PICK_A_VALUE')"
              @update:model-value="handleShopSelect"
            />
            <span v-if="v$.selectedShopId.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.TIKTOK.CHOOSE_PLACEHOLDER') }}
            </span>
          </div>
        </div>
        <div class="w-full">
          <label :class="{ error: v$.inboxName.$error }">
            {{ $t('INBOX_MGMT.ADD.TIKTOK.INBOX_NAME') }}
            <input
              v-model="inboxName"
              type="text"
              :placeholder="$t('INBOX_MGMT.ADD.TIKTOK.PICK_NAME')"
              @input="v$.inboxName.$touch"
            />
            <span v-if="v$.inboxName.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.TIKTOK.ADD_NAME') }}
            </span>
          </label>
        </div>
        <div class="w-full">
          <NextButton :label="$t('INBOX_MGMT.ADD.TIKTOK.CREATE_INBOX')" />
        </div>
      </form>
    </div>
  </div>
</template>
